import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Locations.css';
import './MyStock.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TYPE_LABEL = {
  WAREHOUSE:       'Warehouse',
  DELIVERY_BOY:    'Delivery Boy',
  STORE:           'Store',
  VENDING_MACHINE: 'Vending Machine',
  KITCHEN:         'Kitchen',
};

/* ── My Stock transfer modal (delivery loc → anywhere) ───── */
const TransferModal = ({ location, allLocations, auth, onClose, onDone }) => {
  const [toLocationId, setToLocationId] = useState('');
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const otherLocations = allLocations.filter(l => l.id !== location.id && l.is_active);

  useEffect(() => {
    axios.get(`${API_URL}/stock/balance`, { params: { location_id: location.id }, ...auth })
      .then(res => setItems((res.data || []).map(b => ({ ...b, transferQty: '' }))))
      .catch(() => setError('Failed to load stock.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const setQty = (productId, value) =>
    setItems(prev => prev.map(b => b.product_id === productId ? { ...b, transferQty: value } : b));

  const handleSubmit = async () => {
    if (!toLocationId) { setError('Please select a destination location.'); return; }
    const lines = items
      .filter(b => parseFloat(b.transferQty) > 0)
      .map(b => ({ product_id: b.product_id, quantity: parseFloat(b.transferQty) }));
    if (!lines.length) { setError('Enter a quantity for at least one product.'); return; }
    const over = items.find(b => parseFloat(b.transferQty) > parseFloat(b.qty_on_hand));
    if (over) { setError(`Quantity for "${over.product_name}" exceeds available stock.`); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API_URL}/stock/transfers`, {
        from_location_id: location.id,
        to_location_id:   Number(toLocationId),
        lines,
      }, auth);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="loc-overlay" onClick={onClose}>
      <div className="loc-modal loc-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="loc-modal-header">
          <h3 className="loc-modal-title">Transfer Stock from {location.name}</h3>
          <button className="loc-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="loc-modal-body loc-modal-body--table">
          {error && <div className="loc-modal-error">{error}</div>}
          <div className="loc-field loc-field--inline">
            <label className="loc-label">To Location</label>
            <select className="loc-form-select" value={toLocationId} onChange={e => setToLocationId(e.target.value)}>
              <option value="">— select destination —</option>
              {otherLocations.map(l => (
                <option key={l.id} value={l.id}>{l.name} ({TYPE_LABEL[l.location_type] || l.location_type})</option>
              ))}
            </select>
          </div>
          {loading ? <div className="loc-loading">Loading stock…</div>
            : items.length === 0 ? <div className="loc-empty">No stock on hand to transfer.</div>
            : (
              <table className="loc-stock-table loc-stock-table--transfer">
                <thead><tr>
                  <th>Product</th>
                  <th style={{ textAlign: 'right' }}>On Hand</th>
                  <th style={{ textAlign: 'right' }}>Transfer Qty</th>
                </tr></thead>
                <tbody>
                  {items.map(b => (
                    <tr key={b.product_id}>
                      <td>{b.product_name}</td>
                      <td style={{ textAlign: 'right', color: '#888' }}>
                        {Math.floor(parseFloat(b.qty_on_hand))}{b.unit ? ` ${b.unit}` : ''}
                      </td>
                      <td>
                        <input className="loc-input loc-input--sm loc-input--right"
                          type="number" min="0" step="0.001" max={b.qty_on_hand}
                          placeholder="0" value={b.transferQty}
                          onChange={e => setQty(b.product_id, e.target.value)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
        <div className="loc-modal-footer">
          <button className="loc-btn loc-btn--cancel" onClick={onClose}>Cancel</button>
          <button className="loc-btn loc-btn--save" onClick={handleSubmit} disabled={saving || loading}>
            {saving ? 'Transferring…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Location → my delivery stock transfer modal ─────────── */
const LocationTransferModal = ({ fromLocation, toLocation, auth, onClose, onDone }) => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const defaultQty = fromLocation.location_type !== 'VENDING_MACHINE';

  useEffect(() => {
    axios.get(`${API_URL}/stock/balance`, { params: { location_id: fromLocation.id }, ...auth })
      .then(res => setItems((res.data || []).map(b => ({
        ...b,
        transferQty: defaultQty && Math.floor(parseFloat(b.qty_on_hand)) > 0
          ? String(Math.floor(parseFloat(b.qty_on_hand))) : '',
      }))))
      .catch(() => setError('Failed to load stock.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const setQty = (productId, value) =>
    setItems(prev => prev.map(b => b.product_id === productId ? { ...b, transferQty: value } : b));

  const handleSubmit = async () => {
    const lines = items
      .filter(b => parseFloat(b.transferQty) > 0)
      .map(b => ({ product_id: b.product_id, quantity: parseFloat(b.transferQty) }));
    if (!lines.length) { setError('Enter a quantity for at least one product.'); return; }
    const over = items.find(b => parseFloat(b.transferQty) > parseFloat(b.qty_on_hand));
    if (over) { setError(`Quantity for "${over.product_name}" exceeds available stock.`); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API_URL}/stock/transfers`, {
        from_location_id: fromLocation.id,
        to_location_id:   toLocation.id,
        lines,
      }, auth);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="loc-overlay" onClick={onClose}>
      <div className="loc-modal loc-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="loc-modal-header">
          <h3 className="loc-modal-title">
            {fromLocation.name} → {toLocation.name}
          </h3>
          <button className="loc-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="loc-modal-body loc-modal-body--table">
          {error && <div className="loc-modal-error">{error}</div>}
          {loading ? <div className="loc-loading">Loading stock…</div>
            : items.length === 0 ? <div className="loc-empty">No stock on hand to transfer.</div>
            : (
              <table className="loc-stock-table loc-stock-table--transfer">
                <thead><tr>
                  <th>Product</th>
                  <th style={{ textAlign: 'right' }}>On Hand</th>
                  <th style={{ textAlign: 'right' }}>Transfer Qty</th>
                </tr></thead>
                <tbody>
                  {items.map(b => (
                    <tr key={b.product_id}>
                      <td>{b.product_name}</td>
                      <td style={{ textAlign: 'right', color: '#888' }}>
                        {Math.floor(parseFloat(b.qty_on_hand))}{b.unit ? ` ${b.unit}` : ''}
                      </td>
                      <td>
                        <input className="loc-input loc-input--sm loc-input--right"
                          type="number" min="0" step="1" max={b.qty_on_hand}
                          value={b.transferQty}
                          onChange={e => setQty(b.product_id, e.target.value)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
        <div className="loc-modal-footer">
          <button className="loc-btn loc-btn--cancel" onClick={onClose}>Cancel</button>
          <button className="loc-btn loc-btn--save" onClick={handleSubmit} disabled={saving || loading}>
            {saving ? 'Transferring…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Shared tab: list of locations with transfer icon ─────── */
const LocationsTab = ({ locationType, myLocation, auth, onTransferDone }) => {
  const [locations,      setLocations]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [transferTarget, setTransferTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/stock/locations`, { params: { type: locationType, active: 'true' }, ...auth })
      .then(res => setLocations(res.data || []))
      .catch(() => setError('Failed to load locations.'))
      .finally(() => setLoading(false));
  }, [locationType]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loc-loading">Loading…</div>;
  if (error)   return <div className="loc-empty">{error}</div>;
  if (!locations.length) return <div className="loc-empty">No locations found.</div>;

  return (
    <>
      <div className="mys-loc-grid">
        {locations.map(loc => (
          <div key={loc.id} className="mys-loc-card">
            <div className="mys-loc-card-header">
              <p className="mys-loc-name">{loc.name}</p>
              {myLocation && (
                <button
                  className="loc-icon-btn"
                  title={`Transfer to ${myLocation.name}`}
                  onClick={() => setTransferTarget(loc)}
                >
                  ↔
                </button>
              )}
            </div>
            <p className="mys-loc-count">{loc.product_count} product{loc.product_count !== 1 ? 's' : ''}</p>
          </div>
        ))}
      </div>

      {transferTarget && myLocation && (
        <LocationTransferModal
          fromLocation={transferTarget}
          toLocation={myLocation}
          auth={auth}
          onClose={() => setTransferTarget(null)}
          onDone={() => { setTransferTarget(null); load(); onTransferDone?.(); }}
        />
      )}
    </>
  );
};

/* ── My Stock page ───────────────────────────────────────── */
const MyStock = () => {
  const { user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [activeTab,    setActiveTab]    = useState('my-stock');
  const [myLocation,   setMyLocation]   = useState(null);
  const [stocks,       setStocks]       = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [showTransfer, setShowTransfer] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/stock/my-delivery-stock`, auth),
      axios.get(`${API_URL}/stock/locations`, auth),
    ])
      .then(([stockRes, locRes]) => {
        setMyLocation(stockRes.data.location || null);
        setStocks(stockRes.data.items || []);
        setAllLocations(locRes.data || []);
      })
      .catch(() => setError('Failed to load stock.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { key: 'my-stock', label: 'My Stock' },
    { key: 'stores',   label: 'Store Partners' },
    { key: 'vending',  label: 'Vending Machines' },
  ];

  return (
    <div className="loc-page">
      <div className="loc-header">
        <h1 className="loc-title">My Stock</h1>
      </div>

      {/* Tabs */}
      <div className="mys-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`mys-tab${activeTab === t.key ? ' mys-tab--active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* My Stock tab */}
      {activeTab === 'my-stock' && (
        <>
          {myLocation && stocks.length > 0 && (
            <div className="mys-tab-actions">
              <button className="loc-btn loc-btn--save" onClick={() => setShowTransfer(true)}>
                ↔ Transfer
              </button>
            </div>
          )}
          {loading && <div className="loc-loading">Loading…</div>}
          {!loading && error && <div className="loc-empty">{error}</div>}
          {!loading && !error && !myLocation && (
            <div className="loc-empty">No delivery location assigned to your account.</div>
          )}
          {!loading && !error && myLocation && stocks.length === 0 && (
            <div className="loc-empty">No stock on hand at <strong>{myLocation.name}</strong>.</div>
          )}
          {!loading && !error && myLocation && stocks.length > 0 && (
            <>
              <p className="mys-location-label">{myLocation.name}</p>
              <div className="mys-card-grid">
                {stocks.map(s => (
                  <div key={s.product_id} className="mys-card">
                    <p className="mys-card-name">{s.product_name}</p>
                    <p className="mys-card-qty">{Math.floor(parseFloat(s.qty_on_hand))}</p>
                    <p className="mys-card-label">Qty on Hand</p>
                  </div>
                ))}
              </div>
            </>
          )}
          {showTransfer && myLocation && (
            <TransferModal
              location={myLocation}
              allLocations={allLocations}
              auth={auth}
              onClose={() => setShowTransfer(false)}
              onDone={() => { setShowTransfer(false); load(); }}
            />
          )}
        </>
      )}

      {/* Store Partners tab */}
      {activeTab === 'stores' && (
        <LocationsTab locationType="STORE" myLocation={myLocation} auth={auth} onTransferDone={load} />
      )}

      {/* Vending Machines tab */}
      {activeTab === 'vending' && (
        <LocationsTab locationType="VENDING_MACHINE" myLocation={myLocation} auth={auth} onTransferDone={load} />
      )}
    </div>
  );
};

export default MyStock;
